import { configuredString } from '@/kiosk-core/config/kioskRuntimeConfig';
import { createKioskPaymentRuntime } from '@/kiosk-core/payment/paymentRuntime';
import {
  HOLD_NOT_ACCEPTED_CODE,
  MISSING_READER_FAILURE,
  type PaymentFailure,
  PaymentRefusedError,
  RequestOutcomeUnknownError,
  SESSION_UNAVAILABLE_FAILURE,
} from '@/kiosk-core/payment/paymentStatus';
import {
  createStorageActiveIntentStore,
  type PersistedActiveIntent,
} from '@/kiosk-core/payment/terminalPayment';
import { createApiClient, unwrap } from '@/services/api/client';
import { delay } from '@/services/fetchDriver';
import {
  HttpError,
  HttpParseError,
  NetworkError,
  TimeoutError,
} from '@/services/http';
import { getRuntimeStripeReaderId } from '@/services/urlState';
import { addBreadcrumb, captureException } from '@/telemetry/sentry';
import {
  buildCancelPaymentOnReaderBody,
  buildCapturePaymentIntentBody,
  buildCreatePaymentIntentBody,
  buildPaymentIntentStatusQuery,
  buildSendToReaderBody,
  paymentApiBaseUrl,
} from './kioskPaymentApi';
import { getEntrRequestHeaders } from './requestHeaders';
import type { EntrPaymentTransport } from './types';

export interface KioskPaymentTransportOptions {
  fetchImpl?: typeof fetch;
  apiBaseUrl?: string;
  apiKey?: string | null;
  apiKeyHeader?: string;
  readerId?: string | null;
  paymentDestination?: string | null;
  stripeAccountId?: string | null;
  resolvePaymentDestination?: () => string | null | undefined;
  resolveStripeAccountId?: () => string | null | undefined;
  pollIntervalMs?: number;
  cardWindowMs?: number;
  // Test seams for the clock of the card window and the poll delay.
  now?: () => number;
  wait?: (ms: number) => Promise<void>;
}

const DEFAULT_POLL_INTERVAL_MS = 1_000;
const ACTIVE_INTENT_STORAGE_KEY = 'entr_kiosk.active_payment_intent';
const ACTION_REFUSAL_STATUSES: ReadonlySet<number> = new Set([402, 404, 409]);

function hasErrorCode(error: unknown, status: number, code: string): boolean {
  if (!(error instanceof HttpError) || error.status !== status) return false;
  const body = error.body;
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { code?: unknown }).code === code
  );
}

// v1 refuses a payment for a deleted session, which has no photos to print.
function isSessionRefusal(error: unknown): boolean {
  return hasErrorCode(error, 400, 'PAYMENT_SESSION_INVALID');
}

// The 400 of a hold step has no failure in its body, only the status of the
// intent. It is an expected answer, not a fault of the kiosk.
function holdRefusal(error: unknown): PaymentRefusedError | null {
  if (!hasErrorCode(error, 400, 'HOLD_PAYMENT_INTENT_STATE_ERROR')) return null;
  const details = (
    (error as HttpError).body as {
      details?: { current_status?: unknown };
    } | null
  )?.details;
  const status = details?.current_status;
  return new PaymentRefusedError({
    category: 'canceled',
    code: HOLD_NOT_ACCEPTED_CODE,
    stripe_code: typeof status === 'string' ? status : null,
  });
}

function isPaymentFailure(value: unknown): value is PaymentFailure {
  if (typeof value !== 'object' || value === null) return false;
  const { category, code } = value as Record<string, unknown>;
  return typeof category === 'string' && typeof code === 'string';
}

// A failed v1 payment action answers 402, 404 or 409 with the failure in
// details.failure, and the status read of an unknown intent answers the same
// 404. The same statuses without that field (for example the capture guard)
// stay HTTP errors.
function actionRefusal(error: unknown): PaymentRefusedError | null {
  if (!(error instanceof HttpError)) return null;
  if (!ACTION_REFUSAL_STATUSES.has(error.status)) return null;
  const details = (error.body as { details?: { failure?: unknown } } | null)
    ?.details;
  const failure = details?.failure;
  return isPaymentFailure(failure) ? new PaymentRefusedError(failure) : null;
}

// The backend can have done the request also when the kiosk got no answer.
function hasUnknownOutcome(error: unknown): boolean {
  return (
    error instanceof TimeoutError ||
    error instanceof NetworkError ||
    error instanceof HttpParseError ||
    (error instanceof HttpError && error.status >= 500)
  );
}

async function withActionRefusal<T>(call: Promise<T>): Promise<T> {
  try {
    return await call;
  } catch (error) {
    throw actionRefusal(error) ?? error;
  }
}

// For a request that the reader acts on: the send and the cancel.
async function withReaderOutcome<T>(call: Promise<T>): Promise<T> {
  try {
    return await withActionRefusal(call);
  } catch (error) {
    throw hasUnknownOutcome(error)
      ? new RequestOutcomeUnknownError(error)
      : error;
  }
}

function getPaymentRequestHeaders(
  options: KioskPaymentTransportOptions
): Record<string, string> {
  return getEntrRequestHeaders({
    apiKey: options.apiKey,
    apiKeyHeader: options.apiKeyHeader,
    envApiKey: import.meta.env.VITE_ENTR_PAYMENT_API_KEY,
    envApiKeyHeader: import.meta.env.VITE_ENTR_PAYMENT_API_KEY_HEADER,
  });
}

export function resolveKioskPaymentReaderId(
  options: KioskPaymentTransportOptions = {}
): string | null {
  // Precedence mirrors `getRuntimePrinter()` in kioskPrintTransport:
  // explicit option > URL/localStorage > baked env > null. Aaron flagged
  // the AID 109/110 swap risk — operators MUST be able to re-assign the
  // physical reader per install via ?stripe_reader_id=tmr_... without
  // a rebuild. The baked envs (VITE_*_STRIPE_READER_ID) are fallbacks
  // for dev / single-reader staging only.
  return (
    configuredString(options.readerId) ||
    configuredString(getRuntimeStripeReaderId()) ||
    configuredString(import.meta.env.VITE_ENTR_STRIPE_READER_ID) ||
    configuredString(import.meta.env.VITE_STRIPE_TERMINAL_READER_ID) ||
    null
  );
}

function getSessionStorage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function createKioskPaymentTransport(
  options: KioskPaymentTransportOptions = {}
): EntrPaymentTransport {
  const api = createApiClient({
    baseUrl: options.apiBaseUrl ?? paymentApiBaseUrl(),
    fetchImpl: options.fetchImpl,
    headers: () => getPaymentRequestHeaders(options),
  });
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;

  const runtime = createKioskPaymentRuntime(
    {
      activeIntentStore: createStorageActiveIntentStore(
        getSessionStorage(),
        ACTIVE_INTENT_STORAGE_KEY
      ),
      wait: options.wait ?? delay,
      ...(options.now ? { now: options.now } : {}),
      observer: {
        recordEvent: addBreadcrumb,
        recordException: captureException,
      },
      async createPaymentIntent(input) {
        try {
          return await unwrap(
            api.POST('/v1/payment-intents', {
              body: buildCreatePaymentIntentBody(input),
              ...api.transport({ maxRetries: 1 }),
            })
          );
        } catch (error) {
          if (isSessionRefusal(error)) {
            throw new PaymentRefusedError(SESSION_UNAVAILABLE_FAILURE);
          }
          throw error;
        }
      },
      // No retry: the reader can have the action of a send without an answer,
      // and Stripe refuses a second send as terminal_reader_busy. The attempt
      // reads the reader first (readReaderAction), and sends again only when
      // the reader has no action of the intent.
      sendPaymentIntentToReader(input) {
        return withReaderOutcome(
          unwrap(
            api.POST('/v1/payment-intents/{payment_intent_id}/send-to-reader', {
              params: { path: { payment_intent_id: input.paymentIntentId } },
              body: buildSendToReaderBody(input),
              ...api.transport({ maxRetries: 0 }),
            })
          )
        );
      },
      checkPaymentIntentStatus(input) {
        // The poll loop counts each failed check. One retry per check keeps
        // the same count as before, so the GET does not use the default of 3.
        return withActionRefusal(
          unwrap(
            api.GET('/v1/payment-intents/{payment_intent_id}', {
              params: {
                path: { payment_intent_id: input.paymentIntentId },
                query: buildPaymentIntentStatusQuery(input),
              },
              ...api.transport({ maxRetries: 1 }),
            })
          )
        );
      },
      async readReaderAction(input) {
        const answer = await withActionRefusal(
          unwrap(
            api.GET('/v1/payment-intents/{payment_intent_id}', {
              params: {
                path: { payment_intent_id: input.paymentIntentId },
                query: buildPaymentIntentStatusQuery(input),
              },
              ...api.transport({ maxRetries: 1 }),
            })
          )
        );
        if (!answer.reader) return null;
        return {
          intent: answer,
          actionStatus: answer.reader.action_status ?? null,
          actionPaymentIntent: answer.reader.action_payment_intent ?? null,
        };
      },
      capturePaymentIntent(input) {
        return withActionRefusal(
          unwrap(
            api.POST('/v1/payment-intents/{payment_intent_id}/capture', {
              params: { path: { payment_intent_id: input.paymentIntentId } },
              body: buildCapturePaymentIntentBody(input),
              ...api.transport({ maxRetries: 1 }),
            })
          )
        );
      },
      async holdPaymentIntent(input) {
        // No retry: the next try of the visitor does the hold step again (it
        // is safe to repeat), and a retry here keeps the visitor on the card
        // screen two times as long.
        try {
          return await withActionRefusal(
            unwrap(
              api.POST('/v1/payment-intents/{payment_intent_id}/hold', {
                params: { path: { payment_intent_id: input.paymentIntentId } },
                body: buildCapturePaymentIntentBody(input),
                ...api.transport({ maxRetries: 0 }),
              })
            )
          );
        } catch (error) {
          throw holdRefusal(error) ?? error;
        }
      },
      async cancelPaymentOnReader(intent: PersistedActiveIntent, cancel) {
        // No inline retry: a failed cancel deliberately preserves the intent as
        // "recoverable" (terminalPayment.ts) so it is re-cancelled on the next
        // reset / idle / orphan-sweep, rather than hammering the reader. That
        // recoverable-intent path IS the retry mechanism — adding maxRetries
        // here duplicates it and would drop the preserve-for-recovery contract.
        const body = await withReaderOutcome(
          unwrap(
            api.POST('/v1/payment-intents/cancel-reader', {
              body: buildCancelPaymentOnReaderBody(intent, cancel.releaseHold),
              ...api.transport({ maxRetries: 0 }),
            })
          )
        );
        return { paymentIntentStatus: body?.payment_intent_status ?? null };
      },
    },
    {
      pollIntervalMs,
      ...(options.cardWindowMs === undefined
        ? {}
        : { cardWindowMs: options.cardWindowMs }),
    }
  );
  function initializeRuntime(): void {
    runtime.initialize({
      terminalPaymentEnabled: true,
      readerId: resolveKioskPaymentReaderId(options),
      paymentDestination:
        configuredString(options.paymentDestination) ??
        configuredString(options.resolvePaymentDestination?.()) ??
        'platform',
      stripeAccountId:
        configuredString(options.stripeAccountId) ??
        configuredString(options.resolveStripeAccountId?.()),
    });
  }

  return {
    async payCard(input) {
      try {
        initializeRuntime();
      } catch {
        return resolveKioskPaymentReaderId(options)
          ? { ok: false, reason: 'network-error' }
          : {
              ok: false,
              reason: 'network-error',
              failure: MISSING_READER_FAILURE,
            };
      }
      return runtime.payCard(input);
    },
    async cancelActivePayment() {
      try {
        initializeRuntime();
      } catch {
        return;
      }
      await runtime.cancelActivePayment();
    },
  };
}
